import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  type DocumentSnapshot,
  type FirestoreDataConverter,
  type FirestoreError,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  setDoc,
  type SnapshotOptions,
  type Unsubscribe,
  where,
  type WithFieldValue,
  writeBatch,
} from 'firebase/firestore';
import removeAccents from 'remove-accents';
import { getFirestoreInstance } from './firestoreClient';

export const firestoreProductCategoryConverter:
FirestoreDataConverter<Pagcomanda.ProductCategory> = {
  toFirestore(category: WithFieldValue<Pagcomanda.ProductCategory>) {
    return category;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.ProductCategory {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Product category document data is empty');
    }

    return data as Pagcomanda.ProductCategory;
  },
};

export const firestoreProductConverter: FirestoreDataConverter<Pagcomanda.Product> = {
  toFirestore(product: WithFieldValue<Pagcomanda.Product>) {
    return product;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.Product {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Product document data is empty');
    }

    return data as Pagcomanda.Product;
  },
};

export const productCategoryConverter = firestoreProductCategoryConverter;
export const productConverter = firestoreProductConverter;

function productsCollection(venueId: string) {
  return collection(
    getFirestoreInstance(),
    'venues',
    venueId,
    'products',
  ).withConverter(firestoreProductConverter);
}

function productCategoriesCollection(venueId: string) {
  return collection(
    getFirestoreInstance(),
    'venues',
    venueId,
    'productsCategories',
  ).withConverter(firestoreProductCategoryConverter);
}

function productCategoryDocument(venueId: string, categoryId: string) {
  return doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'productsCategories',
    categoryId,
  );
}

function prepareProductCategoryData(
  category: Partial<Pagcomanda.ProductCategory>,
): WithFieldValue<Pagcomanda.ProductCategory> {
  const prepared: Partial<Pagcomanda.ProductCategory> & Record<string, unknown> = { ...category };

  if (prepared.description && !prepared.parsedName) {
    prepared.parsedName = removeAccents(prepared.description).toLowerCase();
  }

  return prepared as WithFieldValue<Pagcomanda.ProductCategory>;
}

function prepareProductData(
  venueId: string,
  product: Partial<Pagcomanda.Product>,
): WithFieldValue<Pagcomanda.Product> {
  const prepared: Partial<Pagcomanda.Product> & Record<string, unknown> = { ...product };

  if (prepared.categoryId) {
    prepared.category = productCategoryDocument(venueId, prepared.categoryId);
  } else {
    delete prepared.category;
  }

  prepared.parsedName = removeAccents((prepared.description as string | undefined) ?? '').toLowerCase();

  return prepared as WithFieldValue<Pagcomanda.Product>;
}

export async function getVenueProductCategories(
  venueId: string,
  options: { publishedOnly?: boolean; orderByField?: string } = {},
): Promise<QueryDocumentSnapshot<Pagcomanda.ProductCategory>[]> {
  const { publishedOnly = true, orderByField = 'parsedName' } = options;
  const constraints = [];
  if (publishedOnly) {
    constraints.push(where('isPublished', '==', true));
  }
  constraints.push(orderBy(orderByField, 'asc'));

  const q = query(productCategoriesCollection(venueId), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs;
}

export function observeVenueProductCategories(
  venueId: string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.ProductCategory>) => void,
  onError?: (error: FirestoreError) => void,
  options: { publishedOnly?: boolean; orderByField?: string } = {},
): Unsubscribe {
  const { publishedOnly = true, orderByField = 'parsedName' } = options;
  const constraints = [];
  if (publishedOnly) {
    constraints.push(where('isPublished', '==', true));
  }
  constraints.push(orderBy(orderByField, 'asc'));

  const q = query(productCategoriesCollection(venueId), ...constraints);

  return onError ? onSnapshot(q, onNext, onError) : onSnapshot(q, onNext);
}

export async function getVenueProducts(
  venueId: string,
  options: { publishedOnly?: boolean; orderByField?: string } = {},
): Promise<QueryDocumentSnapshot<Pagcomanda.Product>[]> {
  const { publishedOnly = true, orderByField = 'parsedName' } = options;
  const constraints = [];
  if (publishedOnly) {
    constraints.push(where('isPublished', '==', true));
  }
  constraints.push(orderBy(orderByField, 'asc'));

  const q = query(productsCollection(venueId), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs;
}

export function observeVenueProducts(
  venueId: string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.Product>) => void,
  onError?: (error: FirestoreError) => void,
  options: { publishedOnly?: boolean; orderByField?: string } = {},
): Unsubscribe {
  const { publishedOnly = true, orderByField = 'parsedName' } = options;
  const constraints = [];
  if (publishedOnly) {
    constraints.push(where('isPublished', '==', true));
  }
  constraints.push(orderBy(orderByField, 'asc'));

  const q = query(productsCollection(venueId), ...constraints);

  return onError ? onSnapshot(q, onNext, onError) : onSnapshot(q, onNext);
}

export async function getVenueProduct(
  venueId: string,
  productId: string,
): Promise<DocumentSnapshot<Pagcomanda.Product> | null> {
  const ref = doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'products',
    productId,
  ).withConverter(firestoreProductConverter);
  const snapshot = await getDoc(ref);

  return snapshot.exists() ? snapshot : null;
}

export async function addVenueProduct(
  venueId: string,
  product: Pagcomanda.Product,
): Promise<string> {
  const prepared = prepareProductData(venueId, product);
  const docRef = await addDoc(productsCollection(venueId), prepared);

  return docRef.id;
}

export async function saveVenueProduct(
  venueId: string,
  productId: string,
  product: Partial<Pagcomanda.Product>,
): Promise<void> {
  const prepared = prepareProductData(venueId, product);
  const ref = doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'products',
    productId,
  ).withConverter(firestoreProductConverter);

  await setDoc(ref, prepared, { merge: true });
}

export async function deleteVenueProduct(
  venueId: string,
  productId: string,
): Promise<void> {
  const ref = doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'products',
    productId,
  );

  await deleteDoc(ref);
}

export async function addVenueProductCategory(
  venueId: string,
  category: Partial<Pagcomanda.ProductCategory>,
): Promise<string> {
  const prepared = prepareProductCategoryData(category);
  const docRef = await addDoc(productCategoriesCollection(venueId), prepared);

  return docRef.id;
}

export async function saveVenueProductCategory(
  venueId: string,
  categoryId: string,
  category: Partial<Pagcomanda.ProductCategory>,
): Promise<void> {
  const prepared = prepareProductCategoryData(category);
  const ref = doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'productsCategories',
    categoryId,
  ).withConverter(firestoreProductCategoryConverter);

  await setDoc(ref, prepared, { merge: true });
}

export async function deleteVenueProductCategory(
  venueId: string,
  categoryId: string,
): Promise<void> {
  const ref = doc(
    getFirestoreInstance(),
    'venues',
    venueId,
    'productsCategories',
    categoryId,
  );

  await deleteDoc(ref);
}

export async function countVenueProductsInCategory(
  venueId: string,
  categoryId: string,
): Promise<number> {
  const q = query(
    productsCollection(venueId),
    where('category', '==', productCategoryDocument(venueId, categoryId)),
    limit(1),
  );
  const snapshot = await getDocs(q);

  return snapshot.size;
}

export function saveVenueProductCategoriesOrder(
  venueId: string,
  categoryIds: string[],
): Promise<void> {
  const batch = writeBatch(getFirestoreInstance());

  categoryIds.forEach((categoryId, index) => {
    const ref = productCategoryDocument(venueId, categoryId);
    batch.update(ref, { order: index });
  });

  return batch.commit();
}
